import type { ComponentPropsWithoutRef, ReactNode } from "react";
import { cn } from "../utils";

export type PageCtaProps = Omit<ComponentPropsWithoutRef<"section">, "title"> & {
  id: string;
  eyebrow: ReactNode;
  title: ReactNode;
  description: ReactNode;
  icon?: ReactNode;
  decoration?: ReactNode;
  children: ReactNode;
  panelClassName?: string;
  overlayClassName?: string;
  layoutClassName?: string;
};

export type PageCtaPanelProps = Pick<
  PageCtaProps,
  | "id"
  | "eyebrow"
  | "title"
  | "description"
  | "icon"
  | "decoration"
  | "children"
  | "panelClassName"
  | "overlayClassName"
  | "layoutClassName"
>;

export function PageCtaPanel({
  id,
  eyebrow,
  title,
  description,
  icon,
  decoration,
  children,
  panelClassName,
  overlayClassName,
  layoutClassName,
}: PageCtaPanelProps) {
  return (
    <div
      data-page-cta-panel
      className={cn(
        "ornament-panel rounded-2xl garden-wash relative overflow-hidden border border-border bg-card p-6 shadow-panel sm:p-8 lg:p-10",
        panelClassName,
      )}
    >
      <div data-cta-overlay aria-hidden="true" className={cn("absolute inset-0 bg-background/68", overlayClassName)} />
      {decoration}
      <div className={cn("relative grid items-center gap-8 lg:grid-cols-[auto_minmax(0,1fr)_auto]", layoutClassName)}>
        {icon ? (
          <div className="grid size-14 place-items-center rounded-full bg-primary/10 text-primary shadow-sm">
            {icon}
          </div>
        ) : null}
        <div className="max-w-3xl">
          <p className="text-style-overline text-primary">{eyebrow}</p>
          <h2 id={`${id}-title`} className="mt-3 font-display text-3xl font-bold text-balance sm:text-4xl">
            {title}
          </h2>
          <div className="mt-4 leading-relaxed text-muted-foreground">{description}</div>
        </div>
        <div className="flex flex-wrap items-center gap-3">{children}</div>
      </div>
    </div>
  );
}

export function PageCta({
  id,
  eyebrow,
  title,
  description,
  icon,
  decoration,
  children,
  panelClassName,
  overlayClassName,
  layoutClassName,
  className,
  ...props
}: PageCtaProps) {
  return (
    <section id={id} aria-labelledby={`${id}-title`} className={cn("py-section", className)} {...props}>
      <div className="section-shell">
        <PageCtaPanel
          id={id}
          eyebrow={eyebrow}
          title={title}
          description={description}
          icon={icon}
          decoration={decoration}
          panelClassName={panelClassName}
          overlayClassName={overlayClassName}
          layoutClassName={layoutClassName}
        >
          {children}
        </PageCtaPanel>
      </div>
    </section>
  );
}
